import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { SidebarSimpleIcon } from "@phosphor-icons/react/dist/csr/SidebarSimple";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { periodNoun } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import { useReportCurrency } from "@/profitability/currency";
import { MetaList } from "@/profitability/meta-list";
import type { RecordObject } from "@/profitability/records/objects";
import { RecordMark } from "@/profitability/records/record-mark";
import { formatReportPeriod, runDates } from "@/profitability/utils";
import { findingName, findingSentence, findingShortfall } from "./finding-row";
import { FindingRuleLinks } from "./finding-rules";
import { FindingStatusMenu } from "./finding-status";
import { money } from "./labels";
import { useReviewSpec } from "./review-spec";
import { SEVERITY_LABEL } from "./severity-mark";
import type { Finding } from "./types";

export const FindingHeader = ({
  businessId,
  finding,
  object,
  detailsOpen,
  onOpenRecord,
  onBack,
}: {
  businessId: string;
  finding: Finding;
  object: RecordObject | undefined;
  detailsOpen: boolean;
  onOpenRecord: () => void;
  onBack?: () => void;
}) => {
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const name = findingName(finding, object) || (object?.name ?? "Record");
  const title = /^\d/.test(name) ? `${object?.name ?? "Record"} ${name}` : name;
  const noun = (object?.name ?? "record").toLowerCase();
  const sentence = findingSentence(finding);
  const below = findingShortfall(spec, finding);
  const resultLabel =
    object?.dataset.columns.find((column) => column.role === "result")?.label ??
    "Profit";
  const dates = runDates(finding);
  const periodWord = dates
    ? periodNoun(dayjs(dates.end_date).diff(dates.start_date, "day") + 1)
    : "period";
  return (
    <Stack render={<header />} gap={4}>
      <Stack direction="row" align="center" justify="between" gap={3}>
        <Stack direction="row" align="center" gap={2} className="min-w-0">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Back to findings"
              onClick={onBack}
            >
              <CaretLeftIcon aria-hidden />
            </Button>
          )}
          <FindingRuleLinks businessId={businessId} finding={finding} />
        </Stack>
        <Stack direction="row" align="center" gap={2} className="shrink-0">
          <FindingStatusMenu businessId={businessId} finding={finding} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  aria-expanded={detailsOpen}
                  aria-label={
                    detailsOpen
                      ? `Hide ${noun} details`
                      : `View ${noun} details`
                  }
                  onClick={onOpenRecord}
                  className="relative after:absolute after:-inset-1 aria-expanded:bg-neutral-subtle-pressed pointer-coarse:size-11"
                />
              }
            >
              <SidebarSimpleIcon aria-hidden className="rotate-180" />
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end">
              {detailsOpen ? `Hide ${noun} details` : `View ${noun} details`}
            </TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack gap={2}>
        <Stack direction="row" align="center" justify="between" gap={3} wrap>
          <Stack
            direction="row"
            align="center"
            gap={3}
            className="min-w-0 flex-1 basis-48"
          >
            {object && <RecordMark icon={object.icon} name={name} size="md" />}
            <Text
              render={<h2 />}
              data-finding-heading
              tabIndex={-1}
              variant="display"
              className="min-w-0 text-balance [overflow-wrap:anywhere]"
            >
              {title}
            </Text>
          </Stack>
        </Stack>
        <Text variant="caption" tone="muted">
          <MetaList>
            {[
              dates ? formatReportPeriod(dates) : null,
              finding.severity
                ? `${SEVERITY_LABEL[finding.severity]} severity`
                : null,
              finding.occurrences > 1
                ? `Seen ${finding.occurrences} ${periodWord}s`
                : null,
            ]}
          </MetaList>
        </Text>
      </Stack>
      {sentence && <Text className="max-w-prose text-pretty">{sentence}</Text>}
      {below !== null && (
        <Stack gap={1}>
          <Text variant="caption" tone="muted">
            {resultLabel} below average
          </Text>
          <Text
            variant="display"
            tabular
            className={cn({
              "text-danger": finding.margin !== null && finding.margin < 0,
            })}
          >
            −{money(below, currency)}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
